import { useQuery } from '@tanstack/react-query';
import { Controller, FieldError } from 'react-hook-form';
import { useState } from 'react';
import { Autocomplete, CircularProgress, createFilterOptions, Stack, TextField, TextFieldProps } from '@mui/material';

import TextFormLabel from '../text/TextFormLabel';
import TextFormError from '../text/TextFormError';
import { useLocalization } from '../../../localization';
import { datahubAxiosClient } from 'constants/server/apiEndPoint';
import CaretDownIcon from '../icons/CaretDownIcon';

export interface DataSelect {
  label: string;
  value: string;
  isNew?: boolean;
}

export type AutoCompleteInputProps<T = any> = Readonly<
  Partial<
    {
      label: string;
      name: string;
      control: any;
      queryKey: string;
      endpoint: string;
      payload: { [propertyName: string]: any };
      pickedField: DataSelect;
      required: boolean;
      disabled: boolean;
      searchValue: string;
      options: DataSelect[];
      freeSolo: boolean;
      placeholder: string;
      otherError: FieldError;
      isLoading: boolean;
      transformLabel: (item: T) => string;
      onChange: (e: React.SyntheticEvent, option: DataSelect) => void;
      onInputChange: (e: React.SyntheticEvent, value: string) => void;
      customizeLabelDropdown: string;
      noOptionsText: string;
    } & Omit<TextFieldProps, 'value' | 'onChange' | 'name' | 'error' | 'helperText'>
  >
>;

const filter = createFilterOptions();

function AutoCompleteInput<T = any>(props: AutoCompleteInputProps<T>) {
  const { t } = useLocalization();
  const {
    freeSolo = false,
    options = [],
    label,
    name,
    control,
    queryKey = '',
    endpoint = '',
    searchValue = '',
    required = false,
    pickedField,
    payload,
    disabled = false,
    placeholder,
    otherError = undefined,
    isLoading = false,
    customizeLabelDropdown,
    noOptionsText = 'common.messages.resourceNotFound',
    transformLabel,
    onChange,
    onInputChange,
    ...textFieldProps
  } = props;

  //this is to ensure that the input is focused then the API is called
  const [isFocused, setIsFocused] = useState(false);
  const {
    data: optionsData,
    isLoading: isDataLoading,
    isFetching,
  } = useQuery({
    queryKey: [queryKey, searchValue],
    queryFn: async () => {
      const response = await datahubAxiosClient.post(endpoint, {
        ...payload,
      });
      const transformData = response?.data?.results.map((res: any) => ({
        label: transformLabel ? transformLabel(res) : res[pickedField?.label ?? 'name'],
        value: res[pickedField?.value ?? 'value'],
      }));
      return { total: response.data.total, data: transformData };
    },
    enabled: !!queryKey && !!endpoint && isFocused,
    staleTime: 0,
  });

  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      <TextFormLabel label={label ?? ''} required={required} />
      <Controller
        name={(name ?? '') as any}
        control={control}
        render={({ field: { onChange: _, value }, fieldState: { error, isTouched }, formState: { isSubmitted } }) => {
          return (
            <>
              <Autocomplete
                options={optionsData?.data ?? options}
                loading={isDataLoading || isFetching || isLoading}
                loadingText={
                  <Stack justifyContent="center" alignItems="center">
                    <CircularProgress size={20} />
                  </Stack>
                }
                value={value}
                onOpen={() => setIsFocused(true)}
                onChange={onChange}
                onInputChange={onInputChange}
                slotProps={{
                  listbox: {
                    style: {
                      maxHeight: '15.625rem',
                    },
                  },
                }}
                freeSolo={freeSolo}
                filterOptions={
                  freeSolo
                    ? (opts, params) => {
                        const filtered = filter(opts, params);
                        const { inputValue } = params;

                        const isExisting = opts.some(option => option.label === inputValue);
                        if (inputValue !== '' && !isExisting && freeSolo) {
                          filtered.push({
                            label: inputValue,
                            value: inputValue,
                            isNew: true,
                          });
                        }
                        return filtered;
                      }
                    : opts => opts
                }
                noOptionsText={t(noOptionsText)}
                renderOption={(props, option) => (
                  <li {...props} key={`opt-${option.value}`}>
                    {option.isNew && option.value.trim() !== ''
                      ? `${customizeLabelDropdown}: ${option.label}`
                      : option.label}
                  </li>
                )}
                popupIcon={<CaretDownIcon />}
                renderInput={params => (
                  <TextField
                    placeholder={placeholder && t(placeholder)}
                    {...params}
                    error={(!!error || !!otherError) && (isTouched || isSubmitted)}
                    {...textFieldProps}
                    size="small"
                  />
                )}
                disabled={disabled}
              />
              <TextFormError error={error || otherError} />
            </>
          );
        }}
      />
    </Stack>
  );
}

export default AutoCompleteInput;
